import { useQuery, useQueryCache } from '@pinia/colada';
import { computed, onMounted, onScopeDispose, watch } from 'vue';
import { useRouter } from 'vue-router';
import { ApiError } from '../../../shared/api/client.ts';
import { gameKeys, gameViewQuery, noPlayerSession } from '../../../shared/api/game.ts';
import { failedOnTheServer, onStaleView } from '../../../shared/api/serverFailure.ts';
import { route } from '../../../shared/config/routes.ts';
import { identifyPlayer } from '../../../shared/observability/player.ts';

const reasonOfRead = (error: unknown) => (error instanceof ApiError ? error.reason : '');

export function useResyncView() {
  const queryCache = useQueryCache();
  return () => queryCache.invalidateQueries({ key: gameKeys.root }).catch(() => {});
}

export function useGameView(stream: { open: () => void; close: () => void }) {
  const router = useRouter();
  const { data: gameViewRead, error, refresh: fetchGameView } = useQuery(gameViewQuery);
  const gameView = computed(() => (gameViewRead.value === noPlayerSession ? undefined : gameViewRead.value));
  const phase = computed(() => gameView.value?.phase);

  onMounted(async () => {
    try {
      await fetchGameView();
    } catch {
      return;
    }
    stream.open();
  });
  watch(gameViewRead, (read) => {
    if (read !== noPlayerSession) return;

    stream.close();
    router.replace({ name: route.index });
  });
  watch(gameView, (view) => {
    if (view) identifyPlayer(view);
  });

  const resyncFailedOnTheServer = computed(
    () => !!gameView.value && error.value instanceof ApiError && failedOnTheServer(error.value),
  );
  const readRefusal = computed(() => (resyncFailedOnTheServer.value ? '' : reasonOfRead(error.value)));

  const resyncView = useResyncView();
  onScopeDispose(onStaleView(() => void resyncView()));

  return { gameView, phase, readRefusal, resyncView };
}
